<template>
  <div class="assign-engineer-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-icon-wrapper">
            <feather-icon
              icon="UserPlusIcon"
              size="28"
            />
          </div>
          <div class="header-text">
            <h1 class="page-title">
              Assign Engineer
            </h1>
            <p class="page-subtitle">
              Assign an engineer to this request
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Request Info Cards -->
    <div class="info-cards-row">
      <div class="info-card info-card-primary">
        <div class="info-card-icon">
          <feather-icon
            icon="TagIcon"
            size="22"
          />
        </div>
        <div class="info-card-content">
          <span class="info-card-label">Request Number</span>
          <a
            :href="`${BaseUrl.linkToEngineers}/${$route.params.id}?role=${accessRole}&name=${accessName}&email=${accessEmail}&token=${accessToken}`"
            target="_blank"
            class="info-card-value info-card-link"
          >
            <feather-icon
              icon="ExternalLinkIcon"
              size="14"
            />
            {{ $route.query.request_code || '—' }}
          </a>
        </div>
      </div>

      <div class="info-card info-card-certificate">
        <div class="info-card-icon">
          <feather-icon
            icon="AwardIcon"
            size="22"
          />
        </div>
        <div class="info-card-content">
          <span class="info-card-label">Certificate</span>
          <span
            v-if="$route.query.certificate_name_ar || $route.query.certificate_name_en"
            class="info-card-value"
          >
            {{ $route.query.certificate_name_ar && $route.query.certificate_name_en ? `${$route.query.certificate_name_ar} — ${$route.query.certificate_name_en}` : ($route.query.certificate_name_ar || $route.query.certificate_name_en) }}
          </span>
          <span
            v-else
            class="info-card-value info-card-muted"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
            />
            No certificate on this request
          </span>
        </div>
      </div>
      <div class="info-card info-card-sub">
        <div class="info-card-icon">
          <feather-icon
            icon="FileTextIcon"
            size="22"
          />
        </div>
        <div class="info-card-content">
          <span class="info-card-label">Sub Certificate</span>
          <span
            v-if="$route.query.sub_certificate_ar || $route.query.sub_certificate_en"
            class="info-card-value"
          >
            {{ $route.query.sub_certificate_ar && $route.query.sub_certificate_en ? `${$route.query.sub_certificate_ar} — ${$route.query.sub_certificate_en}` : ($route.query.sub_certificate_ar || $route.query.sub_certificate_en) }}
          </span>
          <span
            v-else
            class="info-card-value info-card-muted"
          >
            <feather-icon
              icon="AlertCircleIcon"
              size="14"
            />
            No sub certificate on this request
          </span>
        </div>
      </div>
    </div>

    <!-- Assign Form Card -->
    <div class="form-card">
      <div class="form-card-header">
        <div class="form-card-icon">
          <feather-icon
            icon="Edit3Icon"
            size="22"
          />
        </div>
        <h3 class="form-card-title">
          Assignment Details
        </h3>
      </div>
      <validation-observer
        ref="infoRules"
        v-slot="{ invalid }"
        tag="form"
      >
        <div class="form-card-body">
          <b-row>
            <b-col
              v-if="jobLoading"
              md="6"
              class="d-flex align-items-center justify-content-center py-4"
            >
              <div class="loading-state">
                <b-spinner
                  label="Loading..."
                  variant="primary"
                />
                <span class="loading-text">Loading roles...</span>
              </div>
            </b-col>
            <b-col
              v-if="!jobLoading"
              md="6"
            >
              <validation-provider
                #default="validationContext"
                name="Job"
                rules="required"
              >
                <b-form-group
                  label="Role / Job"
                  label-for="job"
                  class="form-group-custom"
                >
                  <div class="input-with-icon">
                    <feather-icon
                      icon="BriefcaseIcon"
                      size="18"
                      class="input-icon"
                    />
                    <b-form-select
                      id="job"
                      v-model="assignEngineerForm.job"
                      :state="getValidationState(validationContext)"
                      :options="optionJob"
                      class="form-select-custom"
                      trim
                      placeholder="Select a role"
                    />
                  </div>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="loading"
              md="6"
              class="d-flex align-items-center justify-content-center py-4"
            >
              <div class="loading-state">
                <b-spinner
                  label="Loading..."
                  variant="primary"
                />
                <span class="loading-text">Loading engineers...</span>
              </div>
            </b-col>
            <b-col
              v-if="!loading && optionUser.length > 0"
              md="6"
            >
              <validation-provider
                #default="validationContext"
                name="Engineer"
                rules="required"
              >
                <b-form-group
                  label="Engineer"
                  label-for="engineer"
                  class="form-group-custom"
                >
                  <div class="input-with-icon">
                    <feather-icon
                      icon="UserIcon"
                      size="18"
                      class="input-icon"
                    />
                    <b-form-select
                      id="engineer"
                      v-model="assignEngineerForm.user_id"
                      :state="getValidationState(validationContext)"
                      :options="optionUser"
                      class="form-select-custom"
                      trim
                      placeholder="Select an engineer"
                    />
                  </div>
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>

            <b-col
              v-if="!loading && assignEngineerForm.job && optionUser.length === 0"
              md="6"
              class="d-flex align-items-center"
            >
              <div class="empty-state">
                <feather-icon
                  icon="UsersIcon"
                  size="24"
                />
                <span>No engineers available for this role</span>
              </div>
            </b-col>

            <!-- Server Errors -->
            <b-col
              v-if="Object.values(errors).length > 0"
              cols="12"
            >
              <div class="error-alert">
                <feather-icon
                  icon="AlertCircleIcon"
                  size="18"
                />
                <div class="error-content">
                  <p
                    v-for="(error, key, index) in errors"
                    :key="index"
                    class="error-text"
                  >
                    {{ error.toString() }}
                  </p>
                </div>
              </div>
            </b-col>
          </b-row>
        </div>
        <div class="form-card-footer">
          <b-button
            v-if="!loader"
            variant="primary"
            class="btn-submit"
            :disabled="invalid || Object.values(errors).length > 0"
            @click="assignEngineer()"
          >
            <feather-icon
              icon="CheckIcon"
              size="18"
            />
            {{ $route.params.id ? 'Save Changes' : 'Add' }}
          </b-button>
          <b-button
            v-if="loader"
            variant="primary"
            disabled
            class="btn-submit"
          >
            <b-spinner
              small
              type="grow"
              class="mr-50"
            />
            Processing...
          </b-button>
        </div>
      </validation-observer>
    </div>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import axios from 'axios'
import { required } from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
// eslint-disable-next-line no-unused-vars
import _ from 'lodash'
import decryptData from '@/store/auth/decryptData'
import BaseUrl from '@/libs/base-url'

export default {
  components: {},
  data() {
    return {
      id: this.$store.state.generalIds.id,
      accessRole: localStorage.getItem('accessRole') || '',
      accessToken: localStorage.getItem('accessToken') || '',
      accessName: localStorage.getItem('name') || '',
      accessEmail: localStorage.getItem('email') || '',
      required,
      allSelected: [],
      users: [],
      optionUser: [],
      optionJob: [
        { value: 'application reviewer', text: 'Application Reviewer' },
        { value: 'first reviewer', text: 'First Reviewer' },
        { value: 'second reviewer', text: 'Second Reviewer' },
        { value: 'inspector', text: 'Inspector' },
        { value: 'markter', text: 'Markter' },
        { value: 'data entry', text: 'Data Entry' },
        // { value: 'conformity engineer', text: 'Conformity Engineer' },
      ],
      engineers: [],
      loader: false,
      validationErrors: {},
      errors: {},
      loading: false,
      jobLoading: false,
      BaseUrl,
    }
  },
  setup() {
    const { getValidationState } = formValidation()
    // eslint-disable-next-line camelcase

    const assignEngineerForm = ref({
      job: null,
      user_id: null,
    })
    return {
      getValidationState,
      assignEngineerForm,
    }
  },
  watch: {
    'assignEngineerForm.job': function handleJobChange(newJob) {
      if (newJob) {
        this.fetchEngineersByJob(newJob)
        this.setEngineerByJob(newJob)
      } else {
        this.optionUser = []
        this.assignEngineerForm.user_id = null
      }
    },
  },
  async mounted() {
    const { type } = this.$route.params
    const jobMapping = {
      first_reviewer: 'first reviewer',
      second_reviewer: 'second reviewer',
      application_reviewer: 'application reviewer',
      inspector: 'inspector',
      markter: 'markter',
    }

    const selectedJob = jobMapping[type]
    if (selectedJob) {
      await this.getEngineers()
      this.assignEngineerForm.job = selectedJob
      this.setEngineerByJob(selectedJob)
      this.fetchEngineersByJob(selectedJob)
    }
  },

  methods: {
    async getEngineers() {
      this.jobLoading = true
      const url = `requests/${this.$route.params.id}/short-info`
      try {
        const res = await axios.get(url)
        this.engineers = decryptData(res.data.payload).data.request.assigned_to
      } catch (error) {
        this.errors = error.response.data.errors || {}
        this.$toast({
          component: ToastificationContent,
          props: {
            title: `${error.response.data.message}`,
            icon: 'BellIcon',
            variant: 'error',
          },
        })
      } finally {
        this.jobLoading = false
      }
    },
    fetchEngineersByJob(job) {
      this.loading = true
      const url = `engineer/${this.$route.params.id}/job-filter?job=${job}`
      axios.get(url)
        .then(res => {
          this.optionUser = decryptData(res.data.payload).data.engineers.map(item => ({
            value: item.id,
            text: item.name,
          }))
        })
        .catch(error => {
          this.errors = error.response.data.errors || {}
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${error.response.data.message}`,
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    setEngineerByJob(job) {
      const engineer = this.engineers.find(findEngineer => findEngineer.job === job)
      if (engineer) {
        this.assignEngineerForm.user_id = engineer.id
      }
    },

    assignEngineer() {
      if (this.$route.params.id) {
        this.loader = true
        const formData = new FormData()
        Object.keys(this.assignEngineerForm).forEach(key => {
          formData.append(key, this.assignEngineerForm[key])
        })

        axios
          .post(`requests/${this.$route.params.id}/assign`, formData)
          .then(res => {
            if (res.status === 200) {
              this.$toasted.show('Updated Successfully', {
                position: 'top-center',
                duration: 3000,
              })
              this.$router.back()
            }
          })
          .catch(error => {
            if (error.response?.status) {
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: `${error.response.data.message}`,
                  icon: 'BellIcon',
                  variant: 'error',
                },
              })
            }
          })
          .finally(() => {
            this.loader = false
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.assign-engineer-page {
  padding: 0.5rem 0;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  border-radius: 12px;
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(115, 103, 240, 0.35);
}

.header-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.header-icon-wrapper {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  backdrop-filter: blur(4px);
}

.header-text {
  .page-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.25rem 0;
    letter-spacing: -0.02em;
  }

  .page-subtitle {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    margin: 0;
    font-weight: 500;
  }
}

/* Info Cards */
.info-cards-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-card {
  background: #fff;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 16px rgba(34, 41, 47, 0.08);
  }
}

.info-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .info-card-primary & {
    background: rgba(115, 103, 240, 0.12);
    color: #7367f0;
  }

  .info-card-certificate & {
    background: rgba(40, 199, 111, 0.12);
    color: #28c76f;
  }

  .info-card-sub & {
    background: rgba(0, 207, 232, 0.12);
    color: #00cfe8;
  }
}

.info-card-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  min-width: 0;
}

.info-card-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: #b9b9c3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-card-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #5e5873;

  &.info-card-link {
    color: #7367f0;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    transition: color 0.2s;

    &:hover {
      color: #5e50ee;
    }
  }

  &.info-card-muted {
    color: #b9b9c3;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
  }
}

/* Form Card */
.form-card {
  background: #fff;
  border-radius: 12px;
  border: 1px solid #ebe9f1;
  box-shadow: 0 2px 8px rgba(34, 41, 47, 0.06);
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #f8f8f8;
  border-bottom: 1px solid #ebe9f1;
}

.form-card-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
}

.form-card-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #5e5873;
  margin: 0;
}

.form-card-body {
  padding: 1.5rem;
}

.form-group-custom {
  ::v-deep label {
    font-weight: 600;
    font-size: 0.9rem;
    color: #5e5873;
  }
}

.input-with-icon {
  position: relative;
  display: flex;
  align-items: center;

  .input-icon {
    position: absolute;
    left: 12px;
    color: #7367f0;
    pointer-events: none;
  }

  ::v-deep .form-control,
  ::v-deep .custom-select {
    padding-left: 42px;
    border-radius: 8px;
    border: 1px solid #d8d6de;
    min-height: 42px;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.2);
    }
  }
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;

  .loading-text {
    font-size: 0.9rem;
    color: #b9b9c3;
  }
}

.empty-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f8f8;
  border-radius: 8px;
  color: #b9b9c3;
  font-size: 0.9rem;
}

.error-alert {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: rgba(234, 84, 85, 0.08);
  border: 1px solid rgba(234, 84, 85, 0.2);
  border-radius: 8px;
  color: #ea5455;

  .error-content {
    flex: 1;
  }

  .error-text {
    margin: 0 0 0.25rem 0;
    font-size: 0.9rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.form-card-footer {
  padding: 1rem 1.5rem;
  background: #fafafa;
  border-top: 1px solid #ebe9f1;
}

.btn-submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1.25rem;
  font-weight: 600;
  border-radius: 8px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
